/** Matches backend `ALLOWANCE_CENTS` / Profile "current allowance". */
export const ANNUAL_ALLOWANCE = 200

export type AllowanceWindow = {
  start: Date
  end: Date
  resetsOn: Date
}

export function parseHireDate(raw: string | Date | null | undefined): Date | null {
  if (raw instanceof Date) {
    return Number.isNaN(raw.getTime()) ? null : raw
  }
  const s = String(raw || '').trim()
  if (!s) return null
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (iso) {
    const d = new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]))
    return Number.isNaN(d.getTime()) ? null : d
  }
  const d = new Date(s)
  return Number.isNaN(d.getTime()) ? null : d
}

function anniversaryOnYear(hire: Date, year: number) {
  const month = hire.getMonth()
  const day = hire.getDate()
  const candidate = new Date(year, month, day)
  if (candidate.getMonth() !== month) {
    return new Date(year, month + 1, 0)
  }
  return candidate
}

export function anniversaryWindow(hireDateRaw: string | Date | null | undefined, now = new Date()): AllowanceWindow {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const hire = parseHireDate(hireDateRaw)
  if (!hire) {
    const start = new Date(today.getFullYear(), 0, 1)
    const end = new Date(today.getFullYear() + 1, 0, 1)
    return { start, end, resetsOn: end }
  }
  let start = anniversaryOnYear(hire, today.getFullYear())
  if (start > today) start = anniversaryOnYear(hire, today.getFullYear() - 1)
  const end = anniversaryOnYear(hire, start.getFullYear() + 1)
  return { start, end, resetsOn: end }
}

/** Calendar day YYYY-MM-DD so UTC midnight ledger dates match hire-anniversary windows. */
export function toDayStamp(dateRaw: string | Date | null | undefined) {
  if (dateRaw == null || dateRaw === '') return ''
  if (typeof dateRaw === 'string') {
    const match = dateRaw.match(/^(\d{4}-\d{2}-\d{2})/)
    if (match) return match[1]
  }
  const d = dateRaw instanceof Date ? dateRaw : new Date(dateRaw)
  if (Number.isNaN(d.getTime())) return ''
  const iso = d.toISOString()
  if (/T00:00:00(\.\d+)?Z$/.test(iso)) return iso.slice(0, 10)
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

export function dateInWindow(dateRaw: string | Date | null | undefined, window: AllowanceWindow | null | undefined) {
  const day = toDayStamp(dateRaw)
  if (!day || !window?.start || !window?.end) return false
  const start = toDayStamp(window.start)
  const end = toDayStamp(window.end)
  if (!start || !end) return false
  return day >= start && day < end
}

export function isEeapAllowanceCategory(category: string | null | undefined) {
  const key = String(category || '').trim() || 'eeap'
  return key === 'eeap'
}

export function remainingAllowance(employerUsedInWindow: number) {
  const usedCents = Math.round(Number(employerUsedInWindow || 0) * 100)
  if (!Number.isFinite(usedCents)) return ANNUAL_ALLOWANCE
  const remainingCents = Math.min(ANNUAL_ALLOWANCE * 100, Math.max(0, ANNUAL_ALLOWANCE * 100 - usedCents))
  return remainingCents / 100
}

export type AllowanceLedgerRow = {
  employeeId?: string
  date?: string | null
  category?: string
  employerContribution?: number
  scheduled?: boolean
}

/**
 * Remaining $200 LTS allowance for the hire-anniversary year as of `asOfDay`
 * (YYYY-MM-DD). Empty `asOfDay` uses today and includes later posted rows in
 * the current window, matching Profile.
 */
export function remainingAllowancesByEmployee(
  rows: AllowanceLedgerRow[],
  hireDates: Record<string, string | null | undefined>,
  asOfDay: string,
  employeeIds: Iterable<string>,
): Map<string, number> {
  const idSet = new Set([...employeeIds].map((id) => String(id || '')).filter(Boolean))
  const asOf = asOfDay ? parseHireDate(asOfDay) || new Date() : new Date()
  const windows = new Map<string, AllowanceWindow>()
  const usedById = new Map<string, number>()
  for (const id of idSet) {
    windows.set(id, anniversaryWindow(hireDates[id], asOf))
    usedById.set(id, 0)
  }
  for (const row of rows) {
    if (row.scheduled) continue
    const employeeId = String(row.employeeId || '')
    if (!idSet.has(employeeId)) continue
    if (!isEeapAllowanceCategory(row.category)) continue
    const day = toDayStamp(row.date)
    if (asOfDay && day && day > asOfDay) continue
    const window = windows.get(employeeId)
    if (!window || !dateInWindow(row.date, window)) continue
    usedById.set(employeeId, (usedById.get(employeeId) || 0) + Number(row.employerContribution || 0))
  }
  const out = new Map<string, number>()
  for (const id of idSet) {
    out.set(id, remainingAllowance(usedById.get(id) || 0))
  }
  return out
}
